/**
 * Carrusel horizontal.
 *
 * El scroll táctil y por teclado ya lo resuelve el navegador (overflow-x +
 * scroll-snap). Este módulo agrega lo que falta para que en desktop se
 * entienda solo:
 *
 *   - avance automático cada N ms (data-carousel-autoplay), que se pausa
 *     apenas el usuario toma el control;
 *   - desplazamiento continuo al acercar el puntero a los bordes, sin tener
 *     que apretar la flecha;
 *   - arrastre con el mouse;
 *   - barra de posición y flechas que aparecen solo cuando sirven.
 *
 * Todo el movimiento automático se apaga con prefers-reduced-motion: es
 * ayuda, no requisito.
 */

const DRAG_FACTOR = 1.35;
/** Umbral en px para distinguir un arrastre de un click. */
const DRAG_THRESHOLD = 6;

/** Zona sensible de cada borde, en px. Espeja el clamp de carousel.css. */
const EDGE_MIN = 72;
const EDGE_MAX = 128;
const EDGE_RATIO = 0.09;

/** Velocidad del desplazamiento por borde, en px por frame (a 60fps). */
const EDGE_SPEED_MIN = 1.6;
const EDGE_SPEED_MAX = 11;

/** Cuánto espera el autoplay después de que el usuario toca el carrusel. */
const RESUME_DELAY = 6000;
/** Pausa extra al llegar al final, antes de volver al principio. */
const LOOP_PAUSE = 1.6;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initOne(carousel) {
  const viewport = carousel.querySelector('[data-carousel-viewport]');
  if (!viewport) return;

  const prevBtn = carousel.querySelector('[data-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-carousel-next]');
  const thumb = carousel.querySelector('[data-carousel-progress]');

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const reduced = prefersReducedMotion();

  const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);

  /* ---- Estado de bordes, botones y barra -------------------------------- */

  const updateState = () => {
    const max = maxScroll();
    const atStart = viewport.scrollLeft <= 1;
    const atEnd = viewport.scrollLeft >= max - 1;

    carousel.classList.toggle('can-scroll-prev', !atStart && max > 0);
    carousel.classList.toggle('can-scroll-next', !atEnd && max > 0);

    if (prevBtn) prevBtn.disabled = atStart;
    if (nextBtn) nextBtn.disabled = atEnd || max <= 0;

    if (thumb) {
      const visible = viewport.clientWidth / (viewport.scrollWidth || 1);
      thumb.style.setProperty('--thumb-size', `${(visible * 100).toFixed(2)}%`);
      thumb.style.setProperty('--thumb-pos', max > 0 ? (viewport.scrollLeft / max).toFixed(4) : '0');
    }
  };

  /* La zona sensible se calcula acá y se publica al CSS: así el ancho del
     degradé y el área que reacciona al puntero son siempre el mismo número. */
  const measureEdge = () => {
    const zone = Math.min(EDGE_MAX, Math.max(EDGE_MIN, viewport.clientWidth * EDGE_RATIO));
    carousel.style.setProperty('--edge-zone', `${Math.round(zone)}px`);
    return zone;
  };

  let edgeZone = measureEdge();

  viewport.addEventListener('scroll', updateState, { passive: true });
  window.addEventListener('resize', () => {
    edgeZone = measureEdge();
    updateState();
  }, { passive: true });
  updateState();

  /* ---- Avance por pasos --------------------------------------------------- */

  const step = () => {
    const card = viewport.firstElementChild;
    if (!card) return viewport.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(viewport).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  };

  const scrollStep = (dir) => {
    viewport.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
  };

  prevBtn?.addEventListener('click', () => {
    markInteracted();
    scrollStep(-1);
  });
  nextBtn?.addEventListener('click', () => {
    markInteracted();
    scrollStep(1);
  });

  /* Flechas del teclado sobre el viewport (que es focusable por tabindex).
     El navegador ya scrollea con las flechas, pero de a unos pocos px: acá
     se mueve una ficha entera, como con los botones. */
  viewport.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    markInteracted();
    scrollStep(e.key === 'ArrowRight' ? 1 : -1);
  });

  /* ---- Autoplay -----------------------------------------------------------
     Avanza una ficha cada N ms y, al llegar al final, vuelve al principio.
     Se pausa mientras el puntero está encima, mientras algo del carrusel
     tiene el foco, durante un arrastre y cuando la sección no se ve.
     -------------------------------------------------------------------------- */

  const delay = parseInt(carousel.dataset.carouselAutoplay, 10);
  const autoplayOn = Number.isFinite(delay) && delay > 0 && !reduced;

  let timer = null;
  const blockers = new Set();

  const stop = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /* Cadena de setTimeout en vez de setInterval: cada paso decide cuánto se
     espera hasta el siguiente, y así la vuelta al principio puede durar más
     que un avance normal sin quedar pegada para siempre. */
  const schedule = (wait = delay) => {
    stop();
    if (!autoplayOn || blockers.size) return;
    timer = setTimeout(advance, wait);
  };

  const advance = () => {
    timer = null;
    const max = maxScroll();
    if (max <= 0) return schedule();

    if (viewport.scrollLeft >= max - 2) {
      // Volver al inicio es un salto largo: se lo damos con una pausa extra
      // para que no parezca un error.
      viewport.scrollTo({ left: 0, behavior: 'smooth' });
      schedule(delay * LOOP_PAUSE);
      return;
    }
    scrollStep(1);
    schedule();
  };

  /** Cualquier cosa que deba frenar el autoplay se anota con su propio nombre. */
  const block = (reason) => {
    blockers.add(reason);
    stop();
  };

  const unblock = (reason, wait = delay) => {
    blockers.delete(reason);
    if (blockers.size) return;
    schedule(wait);
  };

  /** Marca que el usuario ya entendió el gesto: apaga el cartel de ayuda. */
  const markInteracted = () => {
    carousel.classList.add('has-interacted');
    // Un toque manual gana sobre el automático: le damos aire antes de seguir.
    block('user');
    unblock('user', RESUME_DELAY);
  };

  if (autoplayOn) {
    carousel.addEventListener('pointerenter', () => block('hover'));
    carousel.addEventListener('pointerleave', () => unblock('hover'));
    carousel.addEventListener('focusin', () => block('focus'));
    carousel.addEventListener('focusout', () => unblock('focus'));

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) block('hidden');
      else unblock('hidden');
    });

    // Fuera de pantalla no tiene sentido gastar frames ni adelantar fichas
    // que nadie está viendo.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? unblock('offscreen') : block('offscreen')),
        { threshold: 0.25 }
      );
      io.observe(carousel);
      block('offscreen');
    }

    schedule();
  }

  // El scroll táctil también cuenta como "el usuario tomó el control".
  viewport.addEventListener('touchstart', markInteracted, { passive: true });

  /* ---- De acá para abajo, solo con mouse --------------------------------- */

  if (!finePointer) return;

  /** Puntero que está arrastrando, o null. Lo usan los dos bloques de abajo:
      mientras hay arrastre, el desplazamiento por borde se hace a un lado. */
  let pointerId = null;

  /* ---- Desplazamiento al acercarse a los bordes ---------------------------
     Sin apretar nada: cuanto más cerca del borde está el puntero, más rápido
     corre. Es el gesto que se espera de una fila de fichas que sigue más
     allá de la pantalla.
     -------------------------------------------------------------------------- */

  let edgeDir = 0;
  let edgeSpeed = 0;
  let edgeFrame = null;

  const edgeStep = () => {
    edgeFrame = null;
    if (!edgeDir) return;

    const before = viewport.scrollLeft;
    viewport.scrollLeft = before + edgeDir * edgeSpeed;

    // Llegamos al tope: no seguimos pidiendo frames al pedo.
    if (viewport.scrollLeft === before) {
      setEdge(0, 0);
      return;
    }
    edgeFrame = requestAnimationFrame(edgeStep);
  };

  const setEdge = (dir, speed) => {
    if (dir === edgeDir && speed === edgeSpeed) return;
    edgeDir = dir;
    edgeSpeed = speed;

    carousel.classList.toggle('is-edge-prev', dir < 0);
    carousel.classList.toggle('is-edge-next', dir > 0);
    carousel.classList.toggle('is-edge-scrolling', dir !== 0);

    if (dir && edgeFrame === null) {
      edgeFrame = requestAnimationFrame(edgeStep);
    } else if (!dir && edgeFrame !== null) {
      cancelAnimationFrame(edgeFrame);
      edgeFrame = null;
    }
  };

  if (!reduced) {
    carousel.addEventListener('pointermove', (e) => {
      // Durante un arrastre manda el arrastre.
      if (pointerId !== null) return;

      const rect = viewport.getBoundingClientRect();
      const fromStart = e.clientX - rect.left;
      const fromEnd = rect.right - e.clientX;

      let dir = 0;
      let closeness = 0;

      if (fromStart < edgeZone && fromStart >= 0) {
        dir = -1;
        closeness = 1 - fromStart / edgeZone;
      } else if (fromEnd < edgeZone && fromEnd >= 0) {
        dir = 1;
        closeness = 1 - fromEnd / edgeZone;
      }

      if (!dir) return setEdge(0, 0);

      // Ya está en el tope de ese lado: no marcamos la zona como activa.
      const max = maxScroll();
      if ((dir < 0 && viewport.scrollLeft <= 0) || (dir > 0 && viewport.scrollLeft >= max)) {
        return setEdge(0, 0);
      }

      // Curva cuadrática: cerca del centro apenas se mueve, sobre el borde
      // corre. Lineal se siente brusco apenas entrás en la zona.
      const speed = EDGE_SPEED_MIN + (EDGE_SPEED_MAX - EDGE_SPEED_MIN) * closeness * closeness;
      carousel.classList.add('has-interacted');
      setEdge(dir, speed);
    });

    carousel.addEventListener('pointerleave', () => setEdge(0, 0));
  }

  /* ---- Arrastre con mouse ------------------------------------------------
     Solo para punteros finos: en touch el scroll nativo ya funciona mejor
     y engancharse rompe el gesto.                                          */

  viewport.classList.add('is-draggable');

  let startX = 0;
  let startScroll = 0;
  let moved = 0;

  viewport.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    pointerId = e.pointerId;
    startX = e.clientX;
    startScroll = viewport.scrollLeft;
    moved = 0;
    setEdge(0, 0);
    viewport.classList.add('is-dragging');
  });

  viewport.addEventListener('pointermove', (e) => {
    if (pointerId !== e.pointerId) return;
    const delta = e.clientX - startX;
    moved = Math.abs(delta);
    if (moved > DRAG_THRESHOLD) {
      // Capturamos recién acá: así un click simple sobre una tarjeta
      // sigue llegando a su destino.
      if (!viewport.hasPointerCapture(e.pointerId)) {
        viewport.setPointerCapture(e.pointerId);
      }
      markInteracted();
      viewport.scrollLeft = startScroll - delta * DRAG_FACTOR;
    }
  });

  const endDrag = (e) => {
    if (pointerId !== e.pointerId) return;
    if (viewport.hasPointerCapture(e.pointerId)) {
      viewport.releasePointerCapture(e.pointerId);
    }
    pointerId = null;
    viewport.classList.remove('is-dragging');
  };

  viewport.addEventListener('pointerup', endDrag);
  viewport.addEventListener('pointercancel', endDrag);

  // Tras arrastrar, anulamos el click que el navegador dispara al soltar.
  viewport.addEventListener(
    'click',
    (e) => {
      if (moved > DRAG_THRESHOLD) {
        e.preventDefault();
        e.stopPropagation();
        moved = 0;
      }
    },
    true
  );
}

export function init() {
  document.querySelectorAll('[data-carousel]').forEach(initOne);
}
